import type { Account } from '@xinfer/api-client';

/**
 * Everything needed to make the first request, prefilled.
 *
 * The endpoint is OpenAI-compatible on purpose, so the shortest honest
 * quickstart is "point your existing client at this base URL". Say that
 * plainly rather than inventing a bespoke SDK to document.
 */
export function Quickstart({ account }: { account: Account }) {
  const baseUrl = `${window.location.origin.replace(':5173', ':7000')}/v1`;

  return (
    <>
      <h1>Quickstart</h1>
      <p className="subtitle">
        The API is OpenAI-compatible, so most existing clients work unmodified.
      </p>

      <h2>1. Create a key</h2>
      <p className="muted">
        On the Keys page. It is shown once, so store it before closing the dialog.
      </p>

      <h2>2. Call it with curl</h2>
      <pre className="snippet">
        {`curl ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer $XINFER_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "mock-v4-flash",
    "messages": [{"role": "user", "content": "hello"}]
  }'`}
      </pre>

      <h2>3. Or with the OpenAI SDK</h2>
      <pre className="snippet">
        {`from openai import OpenAI

client = OpenAI(
    api_key=os.environ["XINFER_API_KEY"],
    base_url="${baseUrl}",
)

stream = client.chat.completions.create(
    model="mock-v4-flash",
    messages=[{"role": "user", "content": "hello"}],
    stream=True,
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`}
      </pre>

      <h2>Notes</h2>
      <ul className="muted">
        <li>
          Set <code>&quot;stream&quot;: true</code> for server-sent events; the chunk format
          matches OpenAI&apos;s.
        </li>
        <li>
          Endpoints other than <code>/v1/chat/completions</code> and <code>/v1/models</code>{' '}
          return <code>501</code> rather than pretending to work.
        </li>
        <li>
          Usage is metered in KV-token-seconds, not output tokens — see Limits for why that
          matters for long contexts.
        </li>
        <li className="mono">account: {account.id}</li>
      </ul>
    </>
  );
}
